'use client';

// TODO: this file does not adhere to our routine standards, once botpress sdk is mature and we have more understanding then we should refactor it
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { HebaLoader } from 'components/atomic/atoms';
import { useUIContext } from 'components/context/uiContext/ManagedUiContext';
import StandardLayout from 'components/layout/StandardLayout';
import {
  BOTPRESS_WEBCHAT_SCRIPT_URL,
  WEBCHAT_HEBA_CONFIGURATION
} from 'config/botpressWebchatHeba';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';
import { sendBotpressHebaWelcomeMessageToWebchat } from 'services/planEventWithHeba.service';

const BotpressWebchatHeba = ({
  isBotpressWebchatReadyForConversation,
  router,
  setIsBotpressWebchatReadyForConversation,
  setShowToast
}) => {
  const webchatReference = useRef(null);

  const [isBotpressAvailable, setIsBotpressAvailable] = useState(false);

  const isBotpressWebchatReadyForConversationRef = useRef(
    isBotpressWebchatReadyForConversation
  );

  useEffect(() => {
    isBotpressWebchatReadyForConversationRef.current =
      isBotpressWebchatReadyForConversation;
  }, [isBotpressWebchatReadyForConversation]);

  // TODO: in some wired cases keepAttemptingToOpenBotpressTillConversationIsReady is not invoked  - so we are trying to open it again after 7 seconds
  useEffect(() => {
    setTimeout(() => {
      window.botpress.open();
    }, 7000);
  }, []);

  const keepWaitingForBotpressWebchatConversation = () => {
    let conversationCheckIntervalId;
    const checkBotpressConversationAvailable = () => {
      const isReady = isBotpressWebchatReadyForConversationRef.current;
      if (isReady) {
        clearInterval(conversationCheckIntervalId);
      } else if (window.botpress?._client?._state?.conversationId) {
        // TODO: this is a hack, we should not exploit undocumented properties, they are bound to change
        setIsBotpressWebchatReadyForConversation(true);
      }
    };
    conversationCheckIntervalId = setInterval(
      checkBotpressConversationAvailable,
      1500
    );
  };

  const keepAttemptingToOpenBotpressTillConversationIsReady = () => {
    let intervalId;
    const checkAndOpenBotpress = () => {
      const isReady = isBotpressWebchatReadyForConversationRef.current;
      if (isReady) {
        clearInterval(intervalId);
      }
      window.botpress.open();
    };
    intervalId = setInterval(checkAndOpenBotpress, 1500);
    keepWaitingForBotpressWebchatConversation();
  };

  useEffect(() => {
    const setBotpressEventListeners = () => {
      window.botpress.on('webchat:ready', () => {
        document.querySelector('#webchat')?.removeAttribute('inert');
        keepAttemptingToOpenBotpressTillConversationIsReady();
      });

      window.botpress.on('conversation', () => {
        keepAttemptingToOpenBotpressTillConversationIsReady();
        if (!isBotpressWebchatReadyForConversation) {
          setIsBotpressWebchatReadyForConversation(true);
        }
      });

      window.botpress.on('error', (error) => {
        setShowToast({
          message: `Botpress event error: ${error.message}`,
          show: true,
          status: false
        });
      });

      window.botpress.on('webchat:closed', () => {
        document.querySelector('#webchat')?.setAttribute('inert', 'true');
        router.replace(
          getPageURL({
            pageName: ROUTE_CONFIG.HOME_PAGE.label
          })
        );
      });
    };

    if (isBotpressAvailable) {
      setBotpressEventListeners();
      window.botpress.init(WEBCHAT_HEBA_CONFIGURATION);
    }
  }, [isBotpressAvailable]);

  useEffect(() => {
    if (window?.botpress) {
      window.botpress.open();
      return undefined;
    }

    const scriptWebchat = document.createElement('script');
    const loadBotpressWebchat = () => {
      // scriptWebchat.strategy = 'lazyOnload';
      // scriptWebchat.defer = true; // This tells the browser to download the script in the background but not execute it until after the HTML parsing is complete.
      // scriptWebchat.async = true; // Note: By default, scripts loaded this way are asynchronous.
      // script.blocking = 'render'; // blocking attribute, particularly with the value 'render', is a relatively recent addition to the HTML specification, and browser support can vary. -- might not work on each user's mobile browser as some people do not update browser
      scriptWebchat.src = BOTPRESS_WEBCHAT_SCRIPT_URL;
      scriptWebchat.onload = async () => {
        setIsBotpressAvailable(true);
      };
      scriptWebchat.onerror = () => {
        setShowToast({
          message: 'Error loading Botpress script',
          show: true,
          status: false
        });
      };
      document.body.appendChild(scriptWebchat);
    };

    loadBotpressWebchat();

    return undefined;
  }, []);

  return (
    <>
      {!isBotpressWebchatReadyForConversation && <HebaLoader />}
      <div
        className='webchat-container'
        id='webchat-container'
        ref={webchatReference}
      ></div>
    </>
  );
};

const PlanEventWithHebaWebchat = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const query = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  );

  const { isHostIdentityAvailable } = useUIContext();

  const [showToast, setShowToast] = useState({
    message: '',
    show: false,
    status: false
  });

  const [
    isBotpressWebchatReadyForConversation,
    setIsBotpressWebchatReadyForConversation
  ] = useState(false);
  const [isLeadMarketingReferencePosted, setIsLeadMarketingReferencePosted] =
    useState(false);

  useEffect(() => {
    const insertTracker =
      isBotpressWebchatReadyForConversation &&
      isHostIdentityAvailable &&
      !isLeadMarketingReferencePosted;

    if (insertTracker) {
      sendBotpressHebaWelcomeMessageToWebchat({
        query,
        setIsLeadMarketingReferencePosted,
        setShowToast
      });
    }
  }, [isHostIdentityAvailable, isBotpressWebchatReadyForConversation]);

  return (
    <StandardLayout
      {...{
        setShowToast,
        showHeader: false,
        showLoader: !isBotpressWebchatReadyForConversation,
        showToast
      }}
    >
      <BotpressWebchatHeba
        {...{
          isBotpressWebchatReadyForConversation,
          router,
          setIsBotpressWebchatReadyForConversation,
          setShowToast
        }}
      />
    </StandardLayout>
  );
};

const PlanEventWithHebaWebchatClient = () => (
  <Suspense fallback={<HebaLoader />}>
    <PlanEventWithHebaWebchat />
  </Suspense>
);

export default PlanEventWithHebaWebchatClient;
